<template>
  <Dialog
    :visible="visible"
    :header="isEditing ? 'Edit Development Tip' : 'Create Development Tip'"
    :modal="true"
    :closable="true"
    :close-on-escape="true"
    class="w-full max-w-4xl"
    @update:visible="$emit('update:visible', $event)"
  >
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Dimension Selection -->
      <div class="form-group">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Dimension <span class="text-red-500">*</span>
        </label>
        <Dropdown
          v-model="formData.dimensionValue"
          :options="dimensionOptions"
          option-label="label"
          option-value="value"
          placeholder="Select a dimension"
          class="w-full"
          :class="{ 'p-invalid': errors.dimensionValue }"
        />
        <small v-if="errors.dimensionValue" class="p-error">
          {{ errors.dimensionValue }}
        </small>
      </div>

      <!-- Title Section -->
      <div class="form-group">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Title ({{ $t('BulkImportDialog.optional') }})
        </label>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs text-gray-600 mb-1">
              English ({{ $t('BulkImportDialog.optional') }})
            </label>
            <InputText
              v-model="formData.title.en"
              placeholder="Enter English title"
              class="w-full"
              :class="{ 'p-invalid': errors['title.en'] }"
            />
            <small v-if="errors['title.en']" class="p-error">
              {{ errors['title.en'] }}
            </small>
          </div>
          <div>
            <label class="block text-xs text-gray-600 mb-1">
              German ({{ $t('BulkImportDialog.optional') }})
            </label>
            <InputText
              v-model="formData.title.de"
              placeholder="Enter German title"
              class="w-full"
              :class="{ 'p-invalid': errors['title.de'] }"
            />
            <small v-if="errors['title.de']" class="p-error">
              {{ errors['title.de'] }}
            </small>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="form-group">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Content <span class="text-red-500">*</span>
        </label>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs text-gray-600 mb-1">English</label>
            <Textarea
              v-model="formData.text.en"
              placeholder="Enter English content"
              rows="6"
              class="w-full"
              :class="{ 'p-invalid': errors['text.en'] }"
            />
            <small v-if="errors['text.en']" class="p-error">
              {{ errors['text.en'] }}
            </small>
          </div>
          <div>
            <label class="block text-xs text-gray-600 mb-1">German</label>
            <Textarea
              v-model="formData.text.de"
              placeholder="Enter German content"
              rows="6"
              class="w-full"
              :class="{ 'p-invalid': errors['text.de'] }"
            />
            <small v-if="errors['text.de']" class="p-error">
              {{ errors['text.de'] }}
            </small>
          </div>
        </div>
      </div>

      <!-- Status Toggle -->
      <div class="form-group">
        <div class="flex items-center gap-3">
          <Checkbox
            v-model="formData.active"
            :binary="true"
            input-id="active-status"
          />
          <label for="active-status" class="text-sm font-medium text-gray-700">
            Active (can be sent to users)
          </label>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="$emit('cancel')"
        />
        <Button
          :label="isEditing ? 'Update' : 'Create'"
          :icon="isEditing ? 'pi pi-check' : 'pi pi-plus'"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';

export default {
  name: 'DevTipDialog',
  components: {
    Dialog,
    InputText,
    Textarea,
    Dropdown,
    Checkbox,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    tip: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formData: {
        dimensionValue: '',
        title: { en: '', de: '' },
        text: { en: '', de: '' },
        active: true,
      },
      errors: {},
    };
  },
  computed: {
    ...mapGetters('dimensions', ['getAllDimensions']),
    dimensionOptions() {
      const dimensions = this.getAllDimensions;
      const options = [];
      if (dimensions && dimensions.length > 0) {
        dimensions.forEach((dimension) => {
          options.push({
            label: dimension.translation?.en || dimension.value,
            value: dimension.value,
          });
        });
      }
      return options;
    },
  },
  watch: {
    tip: {
      handler(newTip) {
        if (newTip) {
          this.formData = {
            dimensionValue: newTip.dimensionValue || '',
            title: { ...newTip.title },
            text: { ...newTip.text },
            active: newTip.active !== undefined ? newTip.active : true,
          };
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },
    visible(newVisible) {
      if (!newVisible) {
        this.resetForm();
        this.errors = {};
      }
    },
  },
  async mounted() {
    // Load dimensions if not already loaded
    if (this.getAllDimensions.length === 0) {
      await this.$store.dispatch('dimensions/fetchDimensions');
    }
  },
  methods: {
    ...mapActions('dimensions', ['fetchDimensions']),

    resetForm() {
      this.formData = {
        dimensionValue: '',
        title: { en: '', de: '' },
        text: { en: '', de: '' },
        active: true,
      };
    },

    validateForm() {
      this.errors = {};

      // Validate dimension
      if (!this.formData.dimensionValue) {
        this.errors.dimensionValue = 'Please select a dimension';
      }

      // Titles are optional - no validation needed

      // Validate content
      if (!this.formData.text.en?.trim()) {
        this.errors['text.en'] = 'English content is required';
      }
      if (!this.formData.text.de?.trim()) {
        this.errors['text.de'] = 'German content is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    handleSubmit() {
      if (this.validateForm()) {
        this.$emit('save', { ...this.formData });
      }
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}
</style>
